export const MAX_TRANSLITERATION_CHARACTERS = 100_000

export const TRANSLITERATION_SOURCES = {
  icao:
    'https://www.icao.int/sites/default/files/publications/DocSeries/9303_p3_cons_en.pdf',
  gost:
    'https://protect.gost.ru/gost/details/b426fb19-e5d0-4b97-b1da-bfbc1965f0ce',
} as const

export type TransliterationMode = 'icao' | 'gost' | 'slug' | 'reverse'

export type TransliterationAttempt =
  | { status: 'success'; output: string }
  | { status: 'error'; error: 'character_limit' }

const ICAO_MAP: Readonly<Record<string, string>> = {
  а: 'A', б: 'B', в: 'V', г: 'G', д: 'D', е: 'E', ё: 'E',
  ж: 'ZH', з: 'Z', и: 'I', й: 'I', к: 'K', л: 'L', м: 'M',
  н: 'N', о: 'O', п: 'P', р: 'R', с: 'S', т: 'T', у: 'U',
  ф: 'F', х: 'KH', ц: 'TS', ч: 'CH', ш: 'SH', щ: 'SHCH',
  ъ: 'IE', ы: 'Y', ь: '', э: 'E', ю: 'IU', я: 'IA',
}

const GOST_MAP: Readonly<Record<string, string>> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo',
  ж: 'zh', з: 'z', и: 'i', й: 'j', к: 'k', л: 'l', м: 'm',
  н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u',
  ф: 'f', х: 'x', ц: 'c', ч: 'ch', ш: 'sh', щ: 'shh',
  ъ: "''", ы: 'y', ь: "'", э: 'e', ю: 'yu', я: 'ya',
}

const SLUG_MAP: Readonly<Record<string, string>> = {
  ...GOST_MAP,
  ё: 'e',
  й: 'y',
  х: 'kh',
  ц: 'ts',
  щ: 'shch',
  ъ: '',
  ь: '',
}

const REVERSE_SEQUENCES: ReadonlyArray<readonly [string, string]> = [
  ['shch', 'щ'],
  ['shh', 'щ'],
  ['zh', 'ж'],
  ['kh', 'х'],
  ['ts', 'ц'],
  ['ch', 'ч'],
  ['sh', 'ш'],
  ['yo', 'ё'],
  ['yu', 'ю'],
  ['ya', 'я'],
  ['iu', 'ю'],
  ['ia', 'я'],
  ['ie', 'ъ'],
  ["''", 'ъ'],
  ["'", 'ь'],
  ['a', 'а'],
  ['b', 'б'],
  ['v', 'в'],
  ['g', 'г'],
  ['d', 'д'],
  ['e', 'е'],
  ['z', 'з'],
  ['i', 'и'],
  ['j', 'й'],
  ['k', 'к'],
  ['l', 'л'],
  ['m', 'м'],
  ['n', 'н'],
  ['o', 'о'],
  ['p', 'п'],
  ['r', 'р'],
  ['s', 'с'],
  ['t', 'т'],
  ['u', 'у'],
  ['f', 'ф'],
  ['h', 'х'],
  ['c', 'ц'],
  ['y', 'ы'],
  ['x', 'х'],
]

export function transliterate(
  input: string,
  mode: TransliterationMode,
): TransliterationAttempt {
  if (exceedsCodePointLimit(input, MAX_TRANSLITERATION_CHARACTERS)) {
    return { status: 'error', error: 'character_limit' }
  }

  switch (mode) {
    case 'icao':
      return { status: 'success', output: mapCyrillic(input, ICAO_MAP, true) }
    case 'gost':
      return { status: 'success', output: mapCyrillic(input, GOST_MAP, false) }
    case 'slug':
      return { status: 'success', output: createSlug(input) }
    case 'reverse':
      return { status: 'success', output: reverseTransliterate(input) }
  }
}

function exceedsCodePointLimit(input: string, limit: number): boolean {
  let count = 0
  for (const _character of input) {
    count += 1
    if (count > limit) return true
  }
  return false
}

function mapCyrillic(
  input: string,
  mapping: Readonly<Record<string, string>>,
  forceUppercase: boolean,
): string {
  const converted = input.replace(/[А-ЯЁа-яё]+/g, (word) => {
    const allUppercase = word === word.toLocaleUpperCase('ru-RU')
    return [...word].map((character) => {
      const mapped = mapping[character.toLocaleLowerCase('ru-RU')]
      if (mapped === undefined) return character
      if (forceUppercase || allUppercase) return mapped.toUpperCase()
      return character === character.toLocaleUpperCase('ru-RU')
        ? capitalize(mapped)
        : mapped
    }).join('')
  })

  return forceUppercase ? converted.toUpperCase() : converted
}

function createSlug(input: string): string {
  return mapCyrillic(input, SLUG_MAP, false)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('en-US')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function reverseTransliterate(input: string): string {
  return input.replace(/[A-Za-z']+/g, (word) => {
    const allUppercase = word === word.toUpperCase()
    const startsUppercase = word[0] === word[0]?.toUpperCase()
    const lower = word.toLowerCase()
    let offset = 0
    let output = ''

    while (offset < lower.length) {
      const match = REVERSE_SEQUENCES.find(([sequence]) =>
        lower.startsWith(sequence, offset))
      if (!match) {
        output += word[offset] ?? ''
        offset += 1
        continue
      }
      output += match[1]
      offset += match[0].length
    }

    if (allUppercase) return output.toLocaleUpperCase('ru-RU')
    if (startsUppercase) return capitalize(output)
    return output
  })
}

function capitalize(value: string): string {
  const [first = '', ...rest] = [...value]
  return first.toLocaleUpperCase() + rest.join('')
}
